import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Avatar from '../../assets/svg/avatar.svg';
import daburLogo from '../../assets/svg/appIcon.svg';
import InfiniteScroll from 'react-infinite-scroll-component';
import Markdown from 'markdown-to-jsx';
import classNames from 'classnames';
import {
  createWeatherForcastChat,
  getAllPinCode,
  getWeatherForcastChatStatus,
  getWeatherForcastHistory,
  setChatSessionId,
  setPage,
  setQueryMessage,
} from '../../lib/redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import SkeletonLoader from '../../lib/common/components/loader/SkeletonLoader';
import { CustomLink, preprocessText } from '../../lib/utils/helper';
import FileCard from './FileCard';
import { useLocation } from 'react-router-dom';
import CopyToClipboard from '../../lib/common/components/CopyToClipboard';

import RotatingTextLoader from '../../lib/common/components/loader/RotatingTextLoader';
import PropTypes from 'prop-types';
import { trackEvent, trackException } from '../../lib/utils/telemetryService';
import WelcomeWeatherForecastInterface from './WelcomeWeatherForecastInterface';
import ManageLocationModel from '../ui/weatherForcastModels/ManageLocationModel';
import FetchLocationModel from '../ui/weatherForcastModels/FetchLocationModel';
import BotMarkdown from '../../lib/common/components/markdown/BotMarkdown';
import toast from '../../lib/utils/toast';
import useLoader from '../../hooks/useLoader';
import { ChevronDown, ChevronUp } from 'lucide-react';

export default function WeatherForecastChatInterface({
  isMobile,
  sidebarOpen,
  setChats,
  chats,
}) {
  const location = useLocation();
  const textRef = useRef('');
  const scrollRef = useRef(null);
  const isFetchingRef = useRef(false);

  const [locations, setLocations] = useState([]);
  const dispatch = useDispatch();
  const { selectedChat, page, sessionId, queryMessage } = useSelector(
    state => state.chat,
  );

  const textDelayTimeout = useRef(null);
  const [displayedText, setDisplayedText] = useState('');
  const [tempId, setTempId] = useState(null);
  const inputRef = useRef(null);
  const [inputText, setInputText] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollHeightRef = useRef(0);
  const scrollTimeoutRef = useRef(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isManageLocationOpen, setIsManageLocationOpen] = useState(false);
  const [isFetchLocationOpen, setIsFetchLocationOpen] = useState(false);
  const [pinCodes, setPinCodes] = useState([]);
  const { showLoader, hideLoader } = useLoader()
  const [expandedZipcodes, setExpandedZipcodes] = useState({});
  const toggleZipcode = (chatId, zipcodeKey, defaultOpen = false) => {
    setExpandedZipcodes(prev => {
      const current = prev[chatId] && prev[chatId][zipcodeKey];
      const next = current === undefined ? !defaultOpen : !current;
      return {
        ...prev,
        [chatId]: {
          ...(prev[chatId] || {}),
          [zipcodeKey]: next,
        },
      };
    });
  };
  useEffect(() => {
    handleGetWeatherForecastHistory(1);
    fetchAllPinCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleGetWeatherForecastHistory = async (pageNumber = 1) => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      if (pageNumber === 1) setIsLoadingHistory(true);

      // Store current scroll height before loading new data
      if (scrollRef.current) {
        scrollHeightRef.current = scrollRef.current.scrollHeight;
      }

      const data = {
        page: pageNumber,
        limit: 10,
      };

      const res = await getWeatherForcastHistory(data);
      const newChats = res?.data?.history || [];

      const existingIds = new Set(chats.map(chat => chat._id));
      const uniqueNewChats = newChats.filter(
        chat => !existingIds.has(chat._id),
      );

      const processedMessages = uniqueNewChats.flatMap(chat => [
        {
          role: 'user',
          message: 'Fetch Data',
          _id: chat._id,
          type: 'text',
          date: chat?.createdAt,
        },
        {
          role: 'assistant',
          message: chat?.report,
          _id: `bot-${chat._id}`,
          date: chat?.createdAt,
          pinCodes: chat?.pincode,
        },
      ]);

      setChats(prev => [...processedMessages, ...prev]);

      setHasMore(processedMessages.length > 0);

      // After state update, adjust scroll position
      setTimeout(() => {
        if (scrollRef.current) {
          const newScrollHeight = scrollRef.current.scrollHeight;
          const prevScrollTop = scrollRef.current.scrollTop;
          const scrollDiff = newScrollHeight - scrollHeightRef.current;
          scrollRef.current.scrollTop = prevScrollTop + scrollDiff;
        }
      }, 0);
    } catch (error) {
      console.error(error);
      trackException(error);
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
      if (pageNumber === 1) setIsLoadingHistory(false);
    }
  };

  const fetchMoreData = async () => {
    if (isFetchingRef.current || !hasMore || isLoadingMore) return;

    // Only fetch more if we're near the top
    if (scrollRef.current && scrollRef.current.scrollTop > 100) return;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Set a new timeout
    scrollTimeoutRef.current = setTimeout(async () => {
      const nextPage = page + 1;
      await handleGetWeatherForecastHistory(nextPage);
      setPage(nextPage);
    }, 300); // 300ms debounce
  };

  const handleTextDelay = useCallback(msg => {
    // Clear any existing timeouts
    if (textDelayTimeout.current) {
      clearTimeout(textDelayTimeout.current);
    }

    let currentIndex = 0;
    const typingSpeed = 2;
    textRef.current = ''; // Use ref instead of state for intermediate values

    const typeNextCharacter = () => {
      if (currentIndex < msg.length) {
        textRef.current = msg.substring(0, currentIndex + 1);
        setDisplayedText(textRef.current);
        currentIndex += 1;
        textDelayTimeout.current = setTimeout(typeNextCharacter, typingSpeed);
      }
    };

    typeNextCharacter();

    // Cleanup function
    return () => {
      if (textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (location.pathname === '/weather-forecast') {
      setChats([]);
    }
    // eslint-disable-next-line
  }, [location.pathname]);



  useEffect(() => {
    if (
      loading &&
      queryMessage &&
      sessionId &&
      sessionId === selectedChat?._id
    ) {
      setChats(prev => [...prev, queryMessage]);
    }
    // eslint-disable-next-line
  }, [selectedChat]);

  useEffect(() => {
    if (
      location.pathname !== '/weather-forecast' &&
      selectedChat?._id &&
      (chats?.length === 0 ||
        (chats[chats.length - 1]?._id &&
          !chats[chats.length - 1]._id.includes('temp-user-')))
    ) {
      // Clear existing chats when selecting a new chat
      setChats([]);
      handleGetWeatherForecastHistory(1);
    }
    // eslint-disable-next-line
  }, [selectedChat]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.scrollLeft = inputRef.current.scrollWidth;
    }
  }, [inputText]);

  useEffect(() => {
    if (chats.length > 0) {
      const lastChat = chats[chats.length - 1];
      if (lastChat.role === 'assistant' && lastChat.latest_message) {
        handleTextDelay(lastChat.message);
      } else {
        // Immediately show full message if not latest assistant message
        setDisplayedText(lastChat.message);
      }
    }
  }, [chats, handleTextDelay]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
      } else if (!document.hidden && chats.length > 0) {
        // When returning to the tab, immediately show the full text
        const lastChat = chats[chats.length - 1];
        if (lastChat.role === 'assistant' && lastChat.latest_message) {
          setDisplayedText(lastChat.message);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (textDelayTimeout.current) {
        clearTimeout(textDelayTimeout.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [chats]);

  const handleChatResponse = async ({ payload } = {}) => {
    showLoader()
    let tempSessionId = selectedChat?._id || isSelectedSessionId?.current?._id;
    setTempId(selectedChat?._id || isSelectedSessionId?.current?._id);

    const tempUserMessage = {
      role: 'user',
      message: 'fetch data',
    };
    setInputText('fetch data')
    dispatch(setQueryMessage(tempUserMessage));
    dispatch(setChatSessionId(selectedChat?._id));

    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 100);

    try {
      setLoading(true);

      const textInput = 'fetch data';

      if (!textInput.trim()) {
        return;
      }

      const userMessage = {
        role: 'user',
        message: textInput,
        date: Date.now()
      };

      setChats(prev => [...prev, userMessage]);
      trackEvent('DeepSearch_UserMessageSent', {
        message: textInput,
        sessionId: tempSessionId,
      });

      const res = await createWeatherForcastChat(payload);
      const { data } = res;

      // Add loading message with progress bar
      const loadingMessage = {
        role: 'assistant',
        message: 'Processing your request...',
        _id: `loading-${Date.now()}`,
        type: 'loading',
        progress: 0,
      };
      setChats(prev => [...prev, loadingMessage]);

      if (data) {
        hideLoader()
        setIsFetchLocationOpen(false)
      }

      // Start polling for status
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await getWeatherForcastChatStatus(data?.jobId);
          const statusData = statusRes.data?.jobStatus;
          if (statusData?.status === 'completed') {
            // Update the loading message with the final response
            const finalMessage = {
              role: 'assistant',
              message: statusData?.result || 'No response received.',
              _id: `bot-${Date.now()}`,
              date: Date.now()
            };

            setChats(prev => {
              const updatedChats = prev.map(chat =>
                chat._id === loadingMessage._id ? finalMessage : chat,
              );
              return updatedChats;
            });
            setDisplayedText(finalMessage.message);
            trackEvent('DeepSearch_AssistantResponseReceived', {
              response: finalMessage.message,
              sessionId: finalMessage.sessionId,
            });
            setLoading(false);
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error('Error fetching chat status:', error);
          trackException(error);
          clearInterval(pollInterval);
          setLoading(false);
        }
      }, 15000); // Poll every 15 seconds
    } catch (error) {
      const errorBotMessage = {
        role: 'assistant',
        message: error?.message || 'Something went wrong!!',
        _id: `error-bot-${Date.now()}`,
        statusCode: 'error',
      };
      trackException(error);
      setChats(prev => [...prev, errorBotMessage]);
      setLoading(false);
      hideLoader()
    } finally {
      // setLoading(false);
    }
  };

  const isMicOn = useSelector(state => state.voiceMode.isMicOn);
  const isMicOnRef = useRef(isMicOn);
  const isSelectedSessionId = useRef(selectedChat);

  useEffect(() => {
    isSelectedSessionId.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    isMicOnRef.current = isMicOn;
  }, [isMicOn]);

  // get all pincode
  const fetchAllPinCode = async () => {
    try {
      const res = await getAllPinCode();
      setPinCodes(res?.data?.data);
    } catch (error) {
      toast.error(error)
      setPinCodes([]);
    }
  };



  return (
    <div
      className={`flex-1 flex flex-col overflow-auto sm:overflow-hidden  ${isMobile && sidebarOpen ? 'opacity-50' : ''
        }`}>
      <ManageLocationModel
        isOpen={isManageLocationOpen}
        pinCodes={pinCodes}
        setIsOpen={setIsManageLocationOpen}
        fetchAllPinCode={fetchAllPinCode}
      />
      <FetchLocationModel
        isOpen={isFetchLocationOpen}
        setIsOpen={setIsFetchLocationOpen}
        pinCodes={pinCodes}
        locations={locations}
        setLocations={setLocations}
        handleChatResponse={handleChatResponse}
      />
      {!isLoadingHistory && !selectedChat?._id && chats?.length === 0 && (
        <WelcomeWeatherForecastInterface
          setIsManageLocationOpen={setIsManageLocationOpen}
          handleChatResponse={handleChatResponse}
          setIsFetchLocationOpen={setIsFetchLocationOpen}
          pinCodes={pinCodes}
        />
      )}

      {isLoadingHistory ? (
        <div className="flex h-full items-center justify-center">
          <SkeletonLoader />
        </div>
      ) : chats?.length > 0 ? (
        <div className="flex flex-col  overflow-auto">
          <div
            id="scrollableDiv"
            ref={scrollRef}
            className="flex-1 pt-4 !px-[30px] sm:px-[50px] md:px-[20px] lg:px-[100px] xl:!px-[220px] 2xl:!px-[220px] overflow-y-auto flex flex-col-reverse mt-[40px]">
            {/* <button
              onClick={scrollToBottom}
              className="fixed bottom-36 right-7 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-20">
              <ChevronDown className="w-5 h-5 text-gray-600" />
            </button> */}
            <InfiniteScroll
              inverse={true} // Key for chat-style
              dataLength={chats.length}
              next={fetchMoreData}
              hasMore={hasMore}
              scrollableTarget="scrollableDiv"
              loader={<div className="mx-auto" key={0}></div>}
              endMessage={
                chats?.totalPages >= 2 ? (
                  <p className="text-center m-2 font-segoeUI">No More chats!</p>
                ) : (
                  ''
                )
              }
              scrollThreshold="200px"
              className="overflow-hidden">
              {chats.map((chat, i) =>
                chat.role === 'user' ? (
                  <div
                    key={chat._id || `chat-${i}`}
                    className={`flex group justify-end ${i === 0 ? 'mt-12 mb-8 ' : 'mt-8 mb-8'
                      }`}>
                    <div className="flex flex-row  gap-2">
                      <div className="flex flex-col items-end">
                        <div className="bg-[#1b5c2926] text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] p-[14px] min-h-[44px] justify-center w-fit group">
                          <div className="markDownWrapper !leadings-[27px] font-hel leading-[27px] w-fit whitespace-pre-line break-words font-medium text-sm ">
                            <Markdown
                              key={`chat-${chat?._id}`}
                              options={{
                                overrides: {
                                  a: { component: CustomLink },
                                },
                              }}>
                              {preprocessText(chat.message)}
                            </Markdown>
                          </div>
                          {chat.media && chat.media.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {chat.media.map((mediaItem, fileIndex) => (
                                <FileCard
                                  index={i}
                                  key={`filecard-${mediaItem}`}
                                  mediaItem={mediaItem}
                                  fileIndex={fileIndex}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          {chat?.date && (
                            <div
                              className="text-[12px] text-gray-500 flex items-center gap-1 ml-3"
                              style={{ marginTop: '0px' }}>
                              <span className="ml-2 text-gray-500">
                                {new Date(chat?.date).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                              {" , "}
                              <span>
                                {new Date(chat?.date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>

                            </div>
                          )}
                        </div>
                      </div>
                      {!isMobile && (
                        <div className="!w-[35px] flex-shrink-0">
                          <img
                            src={Avatar}
                            alt="Dabur Logo"
                            className="w-[40px]"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  ((selectedChat?._id &&
                    selectedChat?._id === chat.sessionId) ||
                    !selectedChat?._id) && (
                    <div
                      key={chat._id}
                      className="mt-8 mb-8 max-w-4xl space-y-8">
                      <div className="flex gap-4 w-[280px] sm:w-full">
                        {!isMobile && (
                          <div className="w-[40px]">
                            <img
                              src={daburLogo}
                              alt="Dabur Logo"
                              className="w-[40px]"
                            />
                          </div>
                        )}
                        <div className="text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-lg rounded-br-lg rounded-tr-lg min-h-[44px] justify-center w-fit group">
                          {chat.type === 'loading' ? (
                            // <div className="p-4 border-0  !border-[#1B5C29] rounded-xl bg-white">
                            //   <p className="mb-2 text-base font-medium text-gray-600">
                            //     <RotatingTextLoader messages={[
                                  // "Analyzing location data",
                                  // "Fetching weather models",
                                  // "Processing meteorological trends",
                                  // "Synthesizing 30-day forecast",
                                  // "Finalizing weather report"
                            //     ]} />
                            //   </p>
                            //   <DaburProgressBar progress={chat.progress} />
                            // </div>
                            <span className="p-2">
                                    <RotatingTextLoader messages={[
                                      "Analyzing location data",
                                      "Fetching weather models",
                                      "Processing meteorological trends",
                                      "Synthesizing 30-day forecast",
                                      "Finalizing weather report"
                                    ]} />
                                  </span>
                          ) : (
                            <div className="flex flex-col">
                              <div className="markDownWrapper !leadings-[27px] font-hel w-fit whitespace-pre-line break-words font-medium text-md">
                                {chat?.latest_message &&
                                  i === chats.length - 1 ? (
                                  <BotMarkdown
                                    content={preprocessText(
                                      chat?.isAudio
                                        ? chat?.message
                                        : displayedText,
                                    )}
                                  />
                                ) : Array.isArray(chat.message) ||
                                  typeof chat.message === 'string' ? (
                                  <BotMarkdown
                                    content={preprocessText(chat.message)}
                                  />
                                ) : (
                                  Object.keys(chat.message).map((locationKey, idx, keys) => {
                                    const firstKey = keys[0];
                                    const stored = expandedZipcodes[chat._id] && expandedZipcodes[chat._id][locationKey];
                                    const isOpen = stored !== undefined ? stored : locationKey === firstKey;
                                    return (
                                      <div key={locationKey} className="mb-6">
                                        <button
                                          type="button"
                                          onClick={() => toggleZipcode(chat._id, locationKey, locationKey === firstKey)}
                                          className="w-full flex items-center justify-between font-bold text-lg mb-2 text-left">
                                          <span>{locationKey}</span>
                                          <span className="ml-2">
                                            {isOpen ? (
                                              <ChevronUp className="w-5 h-5" />
                                            ) : (
                                              <ChevronDown className="w-5 h-5" />
                                            )}
                                          </span>
                                        </button>
                                        {isOpen && (
                                          <BotMarkdown
                                            content={preprocessText(
                                              Array.isArray(chat.message[locationKey])
                                                ? chat.message[locationKey].join('\n')
                                                : chat.message[locationKey]
                                            )}
                                          />
                                        )}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <CopyToClipboard
                                  message={
                                    typeof chat.message === "string"
                                      ? chat.message
                                      : Array.isArray(chat.message)
                                        ? chat.message.join("\n")
                                        : Object.entries(chat.message)
                                          .map(([key, value]) => `${key}: ${value}`)
                                          .join("\n") // format object without brackets
                                  }
                                  id={chat._id}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                ),
              )}
            </InfiniteScroll>
          </div>
          <div
            className={classNames(
              '  bottom-0 mb-0 py-4 !px-[30px] sm:px-[50px] md:px-[20px] lg:px-[100px] xl:!px-[220px] 2xl:!px-[220px] ',
              {
                'opacity-50 pointer-events-none':
                  loading && selectedChat?._id === tempId,
              },
            )}>
            <div className=" flex flex-row gap-[10px] items-center justify-end">
              <button
                className="text-[16px] leading-[24px] font-medium  font-inter text-black border border-[#00000033] py-[10px] px-[25px] bg-white rounded-[8px]"
                type="button"
                onClick={() => setIsManageLocationOpen(true)}>
                Manage Location
              </button>

              <button
                onClick={() => setIsFetchLocationOpen(true)}
                disabled={pinCodes.length === 0}
                className={`text-[16px] bg-[#1B5C29] text-white leading-[24px] font-medium font-inter py-[10px] px-[25px] rounded-[8px] ${pinCodes.length === 0 ? 'opacity-40 cursor-not-allowed' : ''
                  }`}>
                Get Weather Forecast
              </button>
            </div>
          </div>
        </div>
      ) : selectedChat?._id ? (
        <div className="flex h-full items-center justify-center">
          <SkeletonLoader />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

WeatherForecastChatInterface.propTypes = {
  isMobile: PropTypes.bool,
  sidebarOpen: PropTypes.bool,
  setChats: PropTypes.func,
  chats: PropTypes.array,
};